<template>
  <div class="search-screen cb-page cb-page--fixed">
    <h1 class="search-screen__title">
      Поиск <span class="cb-glow-text">соперника</span>
    </h1>

    <div class="search-stage" :style="{ '--circle': `${circleSize}px` }">
      <GlowRing class="search-stage__rings" :size="ringSize" :rings="4" arc spin intensity="strong" />

      <CornerFrame
        circle
        :inset="cornerInset"
        :corner-size="cornerSize"
        class="search-stage__circle search-stage__circle--me"
      >
        <PlayerAvatar :src="me && me.avatarUrl" :name="me ? me.playerName : ''" :size="circleSize" />
      </CornerFrame>

      <CornerFrame
        circle
        :inset="cornerInset"
        :corner-size="cornerSize"
        class="search-stage__circle search-stage__circle--opponent"
        :class="{ 'search-stage__circle--found': !!foundOpponent }"
      >
        <Transition name="search-reveal" mode="out-in">
          <PlayerAvatar
            v-if="foundOpponent"
            key="opponent"
            :src="foundOpponent.avatarUrl"
            :name="foundOpponent.playerName"
            :size="circleSize"
          />
          <div v-else key="scan" class="search-stage__scan" aria-hidden="true">
            <AppIcon name="user" :size="Math.round(circleSize * 0.42)" :stroke-width="1.4" class="search-stage__silhouette" />
            <span class="search-stage__scan-line"></span>
            <span class="search-stage__scan-grid"></span>
          </div>
        </Transition>
      </CornerFrame>

      <div class="search-stage__spinner" role="progressbar" aria-label="Идёт поиск соперника">
        <svg viewBox="0 0 100 100" class="search-stage__spinner-svg" aria-hidden="true">
          <circle cx="50" cy="50" r="38" class="search-stage__spinner-track" />
          <circle cx="50" cy="50" r="38" class="search-stage__spinner-arc" />
        </svg>
        <AppIcon v-if="stage === 'found'" name="check" :size="30" class="search-stage__spinner-check" />
      </div>
    </div>

    <div class="search-screen__info">
      <p class="search-screen__caption" role="status" aria-live="polite">{{ caption }}</p>
      <div class="search-screen__meta">
        <span class="search-screen__mode">
          <AppIcon :name="mode === 'MOUTH' ? 'mouth' : 'eye'" :size="18" />
          Режим: {{ modeLabel }}
        </span>
        <span class="search-screen__timer cb-num">{{ timerText }}</span>
      </div>
    </div>

    <div class="search-screen__footer">
      <AppButton variant="ghost" size="sm" @click="cancel">Отмена</AppButton>
    </div>
  </div>
</template>

<script setup>
/**
 * SearchScreen (/search) — макет 3 «Поиск соперника».
 *
 * Логика: камера → ждём открытия WebSocket → create-game(mode) (409 NOT_CONNECTED → ждём
 * переподключения и повторяем) → опрос check-game-status каждые 2 c без наложения запросов →
 * OPPONENT_FOUND → /game. CANCELLED/ERROR/null → одно автоматическое пересоздание, затем тост и «/».
 * Уход с экрана (кроме перехода в игру): cancel-search + освобождение камеры.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { onBeforeRouteLeave, useRouter } from "vue-router";
import AppButton from "@/components/ui/AppButton.vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import CornerFrame from "@/components/ui/CornerFrame.vue";
import GlowRing from "@/components/ui/GlowRing.vue";
import PlayerAvatar from "@/components/ui/PlayerAvatar.vue";
import { cancelSearch, checkGameStatus, createGame, getOpponentInfo } from "@/services/api";
import { acquireCamera, cameraErrorMessage, releaseCamera } from "@/services/media";
import { preloadFaceLandmarker } from "@/services/faceDetection";
import realtime from "@/services/realtime";
import telegram from "@/services/telegram";
import { useGameStore } from "@/stores/game";
import { useSessionStore } from "@/stores/session";
import { useToast } from "@/composables/useToast";
import { formatTimer } from "@/utils/format";
import { MODE_LABELS, normalizeMode } from "@/utils/player";
import { createRoundSounds, playInterfaceSound, stopInterfaceSound } from "@/services/roundSounds";

const POLL_MS = 2000;
const WS_WAIT_MS = 20000;
const CREATE_ATTEMPTS = 4;
const NOT_CONNECTED_RETRY_MS = 1500;
const MAX_POLL_ERRORS = 5;
const OPPONENT_INFO_TIMEOUT_MS = 1500;
const FOUND_REVEAL_MS = 900;

const router = useRouter();
const game = useGameStore();
const session = useSessionStore();
const toast = useToast();
const sounds = createRoundSounds(() => session.notificationsEnabled && realtime.status.value === "open");

/** 'camera' | 'connecting' | 'searching' | 'found' */
const stage = ref("camera");
const foundOpponent = ref(null);
const seconds = ref(0);
const circleSize = ref(140);
const ringSize = ref(360);

const me = computed(() => session.me);
const mode = computed(() => normalizeMode(game.uuid ? game.mode : session.mode));
const modeLabel = computed(() => MODE_LABELS[mode.value]);
const timerText = computed(() => formatTimer(seconds.value));
// Уголки внутри круга: CornerFrame добавляет к inset ещё 6% размера.
const cornerInset = computed(() => Math.round(circleSize.value * 0.12));
const cornerSize = computed(() => Math.round(circleSize.value * 0.19));

const caption = computed(() => {
  switch (stage.value) {
    case "camera":
      return "Включаем камеру…";
    case "connecting":
      return "Подключаемся к серверу…";
    case "found":
      return foundOpponent.value ? `Соперник найден: ${foundOpponent.value.playerName}` : "Соперник найден!";
    default:
      return "Ищем игрока для дуэли";
  }
});

let disposed = false;
let currentUuid = null;
let polling = false;
let pollTimer = null;
let pollErrors = 0;
let recreated = false;
let tickTimer = null;
let revealTimer = null;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function withTimeout(promise, ms) {
  let timer = null;
  return Promise.race([
    promise,
    new Promise((resolve, reject) => {
      timer = setTimeout(() => reject(new Error("timeout")), ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

function errorText(error) {
  if (error && (error.code === "WS_TIMEOUT" || error.code === "WS_CLOSED")) return "Нет соединения с сервером";
  return (error && error.message) || "Не удалось начать поиск";
}

function goHome() {
  if (disposed) return;
  sounds.stop();
  stopInterfaceSound("found");
  router.replace("/");
}

/** Отменить игру на сервере (REST, при ошибке — через WebSocket). */
function cancelRemote(uuid) {
  if (!uuid) return;
  cancelSearch(uuid).catch(() => {
    realtime.send({ type: "cancel_search", gameUUID: uuid });
  });
}

async function createWithRetry() {
  for (let attempt = 1; attempt <= CREATE_ATTEMPTS; attempt += 1) {
    if (!realtime.isOpen()) stage.value = "connecting";
    await realtime.whenOpen(WS_WAIT_MS);
    if (disposed) return null;
    stage.value = "searching";
    try {
      return await createGame(session.mode);
    } catch (error) {
      // Сервер ещё не видит наш WebSocket (переподключение) — ждём и пробуем снова.
      if (error && error.code === "NOT_CONNECTED" && attempt < CREATE_ATTEMPTS && !disposed) {
        stage.value = "connecting";
        await sleep(NOT_CONNECTED_RETRY_MS);
        continue;
      }
      throw error;
    }
  }
  return null;
}

async function startSearch() {
  let created = null;
  try {
    created = await createWithRetry();
  } catch (error) {
    if (disposed) return;
    toast.error(errorText(error));
    goHome();
    return;
  }
  if (!created || !created.uuid) {
    if (!disposed) {
      toast.error("Не удалось начать поиск");
      goHome();
    }
    return;
  }
  if (disposed) {
    // Пользователь ушёл, пока игра создавалась, — не оставляем «висящий» поиск.
    cancelRemote(created.uuid);
    return;
  }
  game.startGame(created);
  currentUuid = created.uuid;
  pollErrors = 0;
  stage.value = "searching";
  sounds.play("search");
  poll();
}

function schedulePoll() {
  clearTimeout(pollTimer);
  pollTimer = disposed ? null : setTimeout(poll, POLL_MS);
}

async function poll() {
  if (disposed || polling || !currentUuid) return;
  const uuid = currentUuid;
  polling = true;
  let response = null;
  let failure = null;
  try {
    response = await checkGameStatus(uuid);
  } catch (error) {
    failure = error;
  } finally {
    polling = false;
  }
  if (disposed || uuid !== currentUuid) return;
  if (failure) {
    sounds.stop("search");
    pollErrors += 1;
    if (pollErrors >= MAX_POLL_ERRORS) {
      toast.error(errorText(failure));
      goHome();
    } else {
      schedulePoll();
    }
    return;
  }
  pollErrors = 0;
  const status = response && response.status;
  if (status === "OPPONENT_FOUND" || status === "READY") {
    onFound(uuid);
  } else if (status === "SEARCHING") {
    sounds.play("search");
    schedulePoll();
  } else {
    onSearchLost();
  }
}

/** Игра отменена/ошибка/неизвестна: одно автоматическое пересоздание. */
async function onSearchLost() {
  sounds.stop("search");
  currentUuid = null;
  if (!recreated) {
    recreated = true;
    stage.value = "searching";
    await startSearch();
    return;
  }
  toast.error("Не удалось найти соперника. Попробуйте ещё раз");
  goHome();
}

async function onFound(uuid) {
  sounds.stop("search");
  // Let the cue finish across the existing 900 ms transition into /game.
  if (stage.value !== "found") playInterfaceSound("found");
  stage.value = "found";
  telegram.haptic("success");
  const startedAt = Date.now();
  try {
    const info = await withTimeout(getOpponentInfo(uuid), OPPONENT_INFO_TIMEOUT_MS);
    if (!disposed && info) {
      game.setOpponent(info);
      foundOpponent.value = game.opponent;
    }
  } catch (error) {
    // экран игры загрузит соперника сам
  }
  if (disposed) return;
  const wait = Math.max(0, FOUND_REVEAL_MS - (Date.now() - startedAt));
  revealTimer = setTimeout(() => {
    if (!disposed) router.replace("/game");
  }, wait);
}

async function run() {
  preloadFaceLandmarker();
  stage.value = "camera";
  try {
    await acquireCamera();
  } catch (error) {
    if (disposed) return;
    toast.error(cameraErrorMessage(error));
    goHome();
    return;
  }
  if (disposed) return;
  await startSearch();
}

function cancel() {
  telegram.haptic("light");
  goHome();
}

function measure() {
  const width = Math.min(window.innerWidth, 480);
  const height = window.innerHeight;
  circleSize.value = Math.round(Math.max(112, Math.min(168, width * 0.38, height * 0.2)));
  ringSize.value = Math.round(Math.min(width + 40, circleSize.value * 2.7));
}

function stopTimers() {
  clearTimeout(pollTimer);
  clearTimeout(revealTimer);
  clearInterval(tickTimer);
  pollTimer = null;
  revealTimer = null;
  tickTimer = null;
}

function dispose(goingToGame) {
  if (disposed) return;
  disposed = true;
  sounds.dispose();
  if (!goingToGame) stopInterfaceSound("found");
  stopTimers();
  if (!goingToGame) {
    cancelRemote(currentUuid || null);
    releaseCamera();
  }
}

onBeforeRouteLeave((to) => {
  dispose(to.name === "game");
});

watch(() => [stage.value, realtime.status.value], () => {
  if (!disposed && stage.value === "searching" && realtime.status.value === "open" && !pollErrors) sounds.play("search");
  else sounds.stop("search");
}, { flush: "sync" });

onMounted(() => {
  measure();
  window.addEventListener("resize", measure);
  tickTimer = setInterval(() => {
    seconds.value += 1;
  }, 1000);
  run();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", measure);
  // Страховка: экран размонтирован без навигации через роутер (обычно dispose уже вызван guard'ом).
  dispose(false);
});
</script>

<style scoped>
.search-screen {
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: center;
}

.search-screen__title {
  margin-top: clamp(24px, calc(var(--cb-vh) * .09), 96px);
  font-size: clamp(30px, 9vw, 38px);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

/* ----------------------------------------------------------- сцена */

.search-stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: calc(var(--circle) * 2.2);
  max-height: calc(var(--cb-vh) * .46);
}

.search-stage__rings {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.search-stage__circle {
  position: relative;
  z-index: 1;
  width: var(--circle) !important;
  height: var(--circle);
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(221, 245, 90, 0.85), 0 0 28px rgba(221, 245, 90, 0.45), 0 12px 30px rgba(0, 0, 0, 0.5);
}

.search-stage__circle--me {
  animation: search-slide-left 0.6s var(--cb-ease) both;
}

.search-stage__circle--opponent {
  animation: search-slide-right 0.6s var(--cb-ease) both;
}

.search-stage__circle--found {
  box-shadow: 0 0 0 3px var(--cb-lime), 0 0 40px rgba(221, 245, 90, 0.7), 0 12px 30px rgba(0, 0, 0, 0.5);
}

.search-stage__scan {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 40%, #222b1b 0%, #10140d 70%);
}

.search-stage__silhouette {
  color: rgba(221, 245, 90, 0.35);
  animation: cb-pulse 1.8s ease-in-out infinite;
}

.search-stage__scan-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(221, 245, 90, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(221, 245, 90, 0.06) 1px, transparent 1px);
  background-size: 14px 14px;
  mask-image: radial-gradient(circle, #000 40%, transparent 72%);
  -webkit-mask-image: radial-gradient(circle, #000 40%, transparent 72%);
}

.search-stage__scan-line {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 30%;
  background: linear-gradient(180deg, rgba(221, 245, 90, 0) 0%, rgba(221, 245, 90, 0.22) 85%, rgba(221, 245, 90, 0.75) 100%);
  animation: search-scan 2.2s ease-in-out infinite;
}

.search-stage__spinner {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 2;
  width: calc(var(--circle) * 0.62);
  height: calc(var(--circle) * 0.62);
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: radial-gradient(circle, #141a10 0%, #0b0e0a 70%);
  box-shadow: 0 0 0 6px rgba(11, 14, 10, 0.9), 0 0 26px rgba(221, 245, 90, 0.25);
}

.search-stage__spinner-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  animation: cb-spin 1.3s linear infinite;
}

.search-stage__spinner-track {
  fill: none;
  stroke: rgba(221, 245, 90, 0.14);
  stroke-width: 10;
}

.search-stage__spinner-arc {
  fill: none;
  stroke: var(--cb-lime);
  stroke-width: 10;
  stroke-linecap: round;
  stroke-dasharray: 90 240;
  filter: drop-shadow(0 0 5px rgba(221, 245, 90, 0.8));
}

.search-stage__spinner-check {
  position: relative;
  color: var(--cb-lime);
  animation: cb-pop-in 0.4s var(--cb-ease) both;
}

/* ----------------------------------------------------------- подписи */

.search-screen__info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.search-screen__caption {
  font-size: 19px;
  font-weight: 500;
  color: var(--cb-text);
  opacity: 0.88;
}

.search-screen__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.search-screen__mode {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: var(--cb-radius-pill);
  background: var(--cb-lime-softer);
  border: 1px solid rgba(221, 245, 90, 0.3);
  color: var(--cb-lime);
  font-size: 13px;
  font-weight: 600;
}

.search-screen__timer {
  color: var(--cb-text-3);
  font-size: 13px;
  font-weight: 600;
}

.search-screen__footer {
  display: flex;
  justify-content: center;
  width: 100%;
  padding-bottom: 4px;
}

.search-reveal-enter-active,
.search-reveal-leave-active {
  transition: opacity 0.3s var(--cb-ease), transform 0.3s var(--cb-ease);
}

.search-reveal-enter-from,
.search-reveal-leave-to {
  opacity: 0;
  transform: scale(0.85);
}

@keyframes search-scan {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(340%);
  }
}

@keyframes search-slide-left {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes search-slide-right {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
